import { metricKeys } from '@pulse/metrics';
import { IMPROVEMENT_OPPORTUNITY_METRICS } from '@src/utils/kpi';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';

type WidgetConfig = {
  metrics: string[];
};

export interface Widget {
  widgetKey: string;
  enabled: boolean;
  config: WidgetConfig;
}

export const SPI_LABEL = {
  EI: 'SPI',
  APP: 'QoE',
  AI: '',
  ODP: '',
};

export const RED_COLOR = '#c32928';
export const BLUE_COLOR = '#85c3ff';
export const GOOD_SESSIONS_NUMBER_COLOR = '#5197DB';

const SPI_METRIC_LIST = {
  EI: metricKeys.StreamingPerformanceIndex,
  APP: metricKeys.AppServicePerformanceIndex,
  AI: null,
};

const SESSIONS_METRICS_LIST = {
  EI: [
    metricKeys.GoodSession,
    metricKeys.GoodSessionAverageLifePlayingTimeMins,
    metricKeys.BadSession,
    metricKeys.BadSessionAverageLifePlayingTimeMins,
    metricKeys.BadSessionPct,
  ],
  AI: [],
  APP: [
    metricKeys.AppSPIGoodSession,
    metricKeys.AppSPIImpactedSession,
    metricKeys.AppSPIImpactedSessionRate,
  ],
  ODP: [],
};

const SESSION_WIDGET_CONFIG_LIST = {
  EI: {
    good: metricKeys.GoodSession,
    goodSecondary: metricKeys.GoodSessionAverageLifePlayingTimeMins,
    bad: metricKeys.BadSession,
    badSecondary: metricKeys.BadSessionAverageLifePlayingTimeMins,
    badRate: metricKeys.BadSessionPct,
  },
  AI: {},
  APP: {
    good: metricKeys.AppSPIGoodSession,
    goodSecondary: null,
    bad: metricKeys.AppSPIImpactedSession,
    badSecondary: null,
    badRate: metricKeys.AppSPIImpactedSessionRate,
  },
  ODP: {},
};

const SPI_EXPORT_METRICS_LIST = {
  EI: [metricKeys.StreamingPerformanceIndex, ...SESSIONS_METRICS_LIST.EI],
  AI: [],
  APP: [metricKeys.AppServicePerformanceIndex, ...SESSIONS_METRICS_LIST.APP],
  ODP: [],
};

export const DEFAULT_AUDIENCE_METRICS_LIST = {
  EI: [metricKeys.Attempts, metricKeys.Plays, metricKeys.ConcurrentPlays],
  AI: [],
  APP: [
    metricKeys.AppSessions,
    metricKeys.UserActiveTime,
    metricKeys.AppTotalEvents,
    metricKeys.DocumentLoads,
    metricKeys.AppSubscribers,
  ],
  ODP: [],
};

export const AUDIENCE_METRICS_LIST = {
  EI: [
    metricKeys.Attempts,
    metricKeys.Plays,
    metricKeys.ConcurrentPlays,
    metricKeys.IntervalMinutesPlayed,
    metricKeys.PercentageComplete,
    metricKeys.EndedPlays,
    metricKeys.Minutes,
    metricKeys.MinutesPerEndedPlay,
    metricKeys.MinutesPerUniqueDevice,
    metricKeys.UniqueDevicesWithAttempts,
    metricKeys.UniqueDevices,
    metricKeys.AdPlays,
    metricKeys.AdPlaysPercentage,
    metricKeys.AdMinutesPlayedPerUniqueDevice,
    metricKeys.AdEndedPlaysPerUniqueDevice,
    metricKeys.AdUniqueDevices,
  ],
  AI: [],
  APP: [
    metricKeys.AppTotalEvents,
    metricKeys.UserActiveTime,
    metricKeys.DocumentLoads,
    metricKeys.AppScreenLoads,
    metricKeys.AppSubscribers,
    metricKeys.AppUniqueDevices,
    metricKeys.AppSessions,
  ],
  ODP: [],
};

export const WIDGET_TYPES = {
  spi: { name: 'spi', displayName: SPI_LABEL[CURRENT_PRODUCT_MODE] },
  streams: { name: 'streams', displayName: 'Good and Impacted Streams' },
  improvementOpportunities: {
    name: 'improvementOpportunities',
    displayName: 'Improvement Opportunities',
  },
  audienceMetrics: { name: 'audienceMetrics', displayName: 'Audience Metrics' },
};

export const spiWidgetConfig = {
  widgetKey: WIDGET_TYPES.spi.name,
  enabled: true,
  config: {
    metrics: [SPI_METRIC_LIST[CURRENT_PRODUCT_MODE]],
  },
};

export const ioWidgetConfig = {
  widgetKey: WIDGET_TYPES.improvementOpportunities.name,
  enabled: true,
  config: {
    metrics: IMPROVEMENT_OPPORTUNITY_METRICS,
  },
};

export const metricsWidget = {
  widgetKey: WIDGET_TYPES.audienceMetrics.name,
  enabled: true,
  config: {
    metrics: DEFAULT_AUDIENCE_METRICS_LIST[CURRENT_PRODUCT_MODE],
  },
};

export const streamsWidgetConfig = {
  widgetKey: WIDGET_TYPES.streams.name,
  enabled: true,
  config: {
    metrics: SESSIONS_METRICS_LIST[CURRENT_PRODUCT_MODE],
  },
};

export const DEFAULT_WIDGET_ORDER: string[] = [
  WIDGET_TYPES.spi.name,
  WIDGET_TYPES.streams.name,
  WIDGET_TYPES.improvementOpportunities.name,
  WIDGET_TYPES.audienceMetrics.name, // new widgets either need to be added here or will get appended in the end
];

export const SPI_WIDGETS = [spiWidgetConfig, ioWidgetConfig, streamsWidgetConfig, metricsWidget];

export const SPI_EXPORT_METRICS = SPI_EXPORT_METRICS_LIST[CURRENT_PRODUCT_MODE];
export const SESSIONS_METRICS = SESSIONS_METRICS_LIST[CURRENT_PRODUCT_MODE];
export const AUDIENCE_METRICS = AUDIENCE_METRICS_LIST[CURRENT_PRODUCT_MODE];
export const DEFAULT_AUDIENCE_METRICS = DEFAULT_AUDIENCE_METRICS_LIST[CURRENT_PRODUCT_MODE];
export const SPI_METRIC = SPI_METRIC_LIST[CURRENT_PRODUCT_MODE];
export const SESSION_WIDGET_CONFIG = SESSION_WIDGET_CONFIG_LIST[CURRENT_PRODUCT_MODE];
